import { useMediaQuery, useTheme } from '@mui/material';
import { RefObject, useEffect, useState } from 'react';

const useResponsiveNavigation = (navRef: RefObject<HTMLDivElement>) => {
  const [isOverlapping, setIsOverlapping] = useState(false);
  const [intersectingTargetsCount, setIntersectingTargetsCount] = useState<number>(
    navRef.current?.childNodes.length || 0
  );
  const [isActive, setIsActive] = useState(false);

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const childLen = navRef.current?.childNodes.length || 0;

    if (childLen > 0) {
      setIntersectingTargetsCount(childLen);
      setIsActive(childLen > 0);
    }
  }, [navRef]);

  useEffect(() => {
    if (!isActive) return;

    const options = {
      root: navRef.current,
      threshold: 1.0, // when 100% of the item is visible or not
    };

    const onIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (!(entry as IntersectionObserverEntry).isIntersecting) {
          setIntersectingTargetsCount((prev) => (prev === 0 || !isActive ? 0 : prev - 1));
        } else {
          setIntersectingTargetsCount((prev) =>
            !isActive ? 0 : prev === navRef.current?.childNodes.length ? navRef.current?.childNodes.length : prev + 1
          );
        }
      });
    };

    const intersectionObserver = new IntersectionObserver(onIntersect, options);

    navRef.current?.childNodes.forEach((item) => {
      intersectionObserver?.observe(item as Element);
    });

    return () => intersectionObserver?.disconnect();
  }, [isActive, navRef]);

  useEffect(() => {
    setIsOverlapping(navRef.current?.childNodes.length !== intersectingTargetsCount && intersectingTargetsCount > 0);
  }, [intersectingTargetsCount, navRef]);

  return { isActive: mdUp, isOverlapping, intersectingTargetsCount: mdUp ? intersectingTargetsCount : 0 };
};

export default useResponsiveNavigation;
